import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';

const TOAST_OPTIONS = {
  position: toast.POSITION.TOP_CENTER,
  draggable: false,
  closeOnClick: false,
  autoClose: 5000,
  hideProgressBar: true,
};

function handleApiError(error) {
  let message = 'An unexpected error occurred';
  if (error.response && error.response.data) {
    const data = error.response.data;
    if (Array.isArray(data)) {
      message = data.join(', ');
    } else if (typeof data === 'string') {
      message = data;
    } else if (data.errors) {
      message = Array.isArray(data.errors) ? data.errors.join(', ') : String(data.errors);
    }
  }
  toast.error(message, TOAST_OPTIONS);
}

export default function useV3Budgets() {
  const { csrfToken, team, dispatch } = useV3OfferContext();

  const setHeaders = useCallback(() => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
  }, [csrfToken]);

  const fetchBudget = useCallback(async (budgetId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await axios.get(`/teams/${team.id}/budgets/v3/${budgetId}`);
      dispatch({ type: V3_ACTIONS.SET_BUDGET, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_LOADING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const updateBudget = useCallback(async (budgetId, budgetParams) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.patch(`/teams/${team.id}/budgets/v3/${budgetId}`, {
        v3_budget: budgetParams,
      });
      dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const applyTemplate = useCallback(async (budgetId, budgetTemplateId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.post(
        `/teams/${team.id}/budgets/v3/${budgetId}/apply_template`,
        { budget_template_id: budgetTemplateId }
      );
      dispatch({ type: V3_ACTIONS.SET_BUDGET, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const createLineItem = useCallback(async (lineItemParams) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const params = {
        ...lineItemParams,
        uuid: lineItemParams.uuid || uuidv4(),
      };
      const response = await axios.post(`/teams/${team.id}/budget_line_items/v3`, {
        v3_budget_line_item: params,
      });
      dispatch({ type: V3_ACTIONS.ADD_LINE_ITEM, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const updateLineItem = useCallback(async (lineItemId, lineItemParams) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.patch(
        `/teams/${team.id}/budget_line_items/v3/${lineItemId}`,
        { v3_budget_line_item: lineItemParams }
      );
      dispatch({ type: V3_ACTIONS.UPDATE_LINE_ITEM, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const deleteLineItem = useCallback(async (lineItemId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      await axios.delete(`/teams/${team.id}/budget_line_items/v3/${lineItemId}`);
      dispatch({ type: V3_ACTIONS.REMOVE_LINE_ITEM, payload: lineItemId });
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const createBudgetBonus = useCallback(async (budgetId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.post(`/teams/${team.id}/budget_bonuses/v3`, {
        v3_budget_bonus: { v3_budget_id: budgetId },
      });
      dispatch({ type: V3_ACTIONS.ADD_BUDGET_BONUS, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const updateBudgetBonus = useCallback(async (bonusId, bonusParams) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.patch(
        `/teams/${team.id}/budget_bonuses/v3/${bonusId}`,
        { v3_budget_bonus: bonusParams }
      );
      dispatch({ type: V3_ACTIONS.UPDATE_BUDGET_BONUS, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const deleteBudgetBonus = useCallback(async (bonusId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      await axios.delete(`/teams/${team.id}/budget_bonuses/v3/${bonusId}`);
      dispatch({ type: V3_ACTIONS.REMOVE_BUDGET_BONUS, payload: bonusId });
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const createDealBracket = useCallback(async (budgetId, attrs = {}) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.post(`/teams/${team.id}/deal_brackets/v3`, {
        v3_deal_bracket: { v3_budget_id: budgetId, ...attrs },
      });
      dispatch({ type: V3_ACTIONS.ADD_BUDGET_DEAL_BRACKET, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const updateDealBracket = useCallback(async (bracketId, bracketParams) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      const response = await axios.patch(
        `/teams/${team.id}/deal_brackets/v3/${bracketId}`,
        { v3_deal_bracket: bracketParams }
      );
      dispatch({ type: V3_ACTIONS.UPDATE_BUDGET_DEAL_BRACKET, payload: response.data });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  const deleteDealBracket = useCallback(async (bracketId) => {
    setHeaders();
    dispatch({ type: V3_ACTIONS.SET_SAVING, payload: true });
    try {
      await axios.delete(`/teams/${team.id}/deal_brackets/v3/${bracketId}`);
      dispatch({ type: V3_ACTIONS.REMOVE_BUDGET_DEAL_BRACKET, payload: bracketId });
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch({ type: V3_ACTIONS.SET_SAVING, payload: false });
    }
  }, [team, setHeaders, dispatch]);

  // Reorder a single bracket to a new 1-based position. acts_as_list renumbers
  // the sibling brackets atomically server-side, so the client sends one PATCH
  // and trusts the server's canonical order (mirrors reorderLineItem). The
  // caller has already applied the optimistic move to the local list.
  const reorderDealBracket = useCallback(async (bracketId, newPosition) => {
    setHeaders();
    try {
      const response = await axios.patch(
        `/teams/${team.id}/deal_brackets/v3/${bracketId}`,
        { v3_deal_bracket: { position: newPosition } }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }, [team, setHeaders]);

  const createTicketLineItem = useCallback(async (budgetId, position) => {
    return createLineItem({
      v3_budget_id: budgetId,
      category: 'TICKET',
      name: `Ticket #${position}`,
      operator: '*',
      left_hand: 0,
      right_hand: 0,
      available: 0,
      comps: 0,
      kills: 0,
    });
  }, [createLineItem]);

  const createIncomeLineItem = useCallback(async (budgetId, position) => {
    return createLineItem({
      v3_budget_id: budgetId,
      category: 'INCOME',
      name: `Other Income #${position}`,
      operator: '*',
      calc_type: 'fixed',
      left_hand: 0,
      // A fixed row totals amount x quantity, so quantity 0 leaves it at $0
      // however large an amount is typed in. The forecast quantity is seeded
      // too because the engine's fallback is asymmetric — the amount treats 0
      // as unset and inherits from Offer, the quantity treats it as set and
      // does not — which is the same pair handleQtyFieldChange writes when a
      // user sets the quantity by hand. (O-4961)
      right_hand: 1,
      right_hand_forecast: 1,
    });
  }, [createLineItem]);

  const createExpenseLineItem = useCallback(async (budgetId, position) => {
    return createLineItem({
      v3_budget_id: budgetId,
      category: 'EXPENSES',
      name: `Expense #${position}`,
      operator: '*',
      calc_type: 'fixed',
      left_hand: 0,
      // A fixed row totals amount x quantity, so quantity 0 leaves it at $0
      // however large an amount is typed in. The forecast quantity is seeded
      // too because the engine's fallback is asymmetric — the amount treats 0
      // as unset and inherits from Offer, the quantity treats it as set and
      // does not — which is the same pair handleQtyFieldChange writes when a
      // user sets the quantity by hand. (O-4961)
      right_hand: 1,
      right_hand_forecast: 1,
    });
  }, [createLineItem]);

  const createAdditionLineItem = useCallback(async (budgetId, position, performerId = null) => {
    return createLineItem({
      v3_budget_id: budgetId,
      category: 'ADDITION',
      name: `Addition #${position}`,
      operator: '*',
      calc_type: 'fixed',
      left_hand: 0,
      // A fixed row totals amount x quantity, so quantity 0 leaves it at $0
      // however large an amount is typed in. The forecast quantity is seeded
      // too because the engine's fallback is asymmetric — the amount treats 0
      // as unset and inherits from Offer, the quantity treats it as set and
      // does not — which is the same pair handleQtyFieldChange writes when a
      // user sets the quantity by hand. (O-4961)
      right_hand: 1,
      right_hand_forecast: 1,
      performer_id: performerId,
    });
  }, [createLineItem]);

  const createDeductionLineItem = useCallback(async (budgetId, position) => {
    return createLineItem({
      v3_budget_id: budgetId,
      category: 'DEDUCTIONS',
      name: `Deduction #${position}`,
      operator: '*',
      calc_type: 'per_tickets_sold',
      left_hand: 0,
      right_hand: 0,
    });
  }, [createLineItem]);

  const reorderLineItem = useCallback(async (lineItemId, newPosition) => {
    setHeaders();
    try {
      const response = await axios.patch(
        `/teams/${team.id}/budget_line_items/v3/${lineItemId}`,
        { v3_budget_line_item: { position: newPosition } }
      );
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }, [team, setHeaders]);

  return {
    fetchBudget,
    updateBudget,
    applyTemplate,
    createLineItem,
    updateLineItem,
    deleteLineItem,
    reorderLineItem,
    createBudgetBonus,
    updateBudgetBonus,
    deleteBudgetBonus,
    createDealBracket,
    updateDealBracket,
    deleteDealBracket,
    reorderDealBracket,
    createTicketLineItem,
    createIncomeLineItem,
    createExpenseLineItem,
    createAdditionLineItem,
    createDeductionLineItem,
  };
}
